import { useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import {
  GET_PERMISSIONS_CATEGORIES,
  PermissionsCategoryResponseData,
} from '../../../../apollo/queries/v3/RolesNPermissions/permissionCategories.queries';
import PermissionCategoryViewModel, {
  PermissionCategory,
} from '../models/PermissionCategoryViewModel';
import {
  AssignedPermissions,
  PermissionCheckArg,
} from '../PermissionCategoryContext';
import {
  mapPermissionsToAssigned,
  resolvePermissionDependencies,
  toPermissionCategoryResponses,
} from '../../utils/permissionDependencies';
import { PermissionToCategory } from '../../../../apollo/queries/v3/RolesNPermissions/getCustomRoleById.queries';

type Props = {
  editUserPermissions: PermissionToCategory[];
};

const dedupeAssignedPermissions = (
  permissions: AssignedPermissions[],
): AssignedPermissions[] => {
  const seen = new Set<string>();
  return permissions.filter(({ permission }) => {
    if (seen.has(permission)) {
      return false;
    }
    seen.add(permission);
    return true;
  });
};

const applyAssignedChecks = (
  pCategories: PermissionCategory[],
  assigned: Array<{ permission: string }>,
): PermissionCategory[] => {
  const assignedPermissionSet = new Set(assigned.map((p) => p.permission));

  return pCategories.map((pc) => {
    const updatedPermissions = pc.permissions.map((per) => ({
      ...per,
      isChecked: assignedPermissionSet.has(per.permission),
    }));

    return {
      ...pc,
      permissions: updatedPermissions,
      isChecked:
        updatedPermissions.length > 0 &&
        updatedPermissions.every((p) => p.isChecked),
    };
  });
};

const usePermissionsCategoryAPI = ({ editUserPermissions }: Props) => {
  const [permissionCategories, setPermissions] = useState<PermissionCategory[]>(
    [],
  );
  const [totalPermissions, setTotalPermissions] = useState(0);
  const [assignedPermissions, setAssignedPermissions] = useState<
    AssignedPermissions[]
  >([]);
  const [searchedText, setSearchedText] = useState('');
  const assignedPermissionsRef = useRef(assignedPermissions);
  assignedPermissionsRef.current = assignedPermissions;

  const assignPermissionForEditUser = () => {
    const per: AssignedPermissions[] = dedupeAssignedPermissions(
      editUserPermissions.map((p) => ({
        permission: p.permission,
        module: p.category,
      })),
    );

    assignedPermissionsRef.current = per;
    setAssignedPermissions(per);
  };

  const updatePermissionCategoryForEditUser = (
    pCategories: PermissionCategory[],
  ) => {
    if (!pCategories.length) {
      return;
    }

    setPermissions(applyAssignedChecks(pCategories, editUserPermissions));
  };

  useEffect(() => {
    assignPermissionForEditUser();
    updatePermissionCategoryForEditUser(permissionCategories);
  }, [editUserPermissions]);

  const { loading, error } = useQuery<PermissionsCategoryResponseData>(
    GET_PERMISSIONS_CATEGORIES,
    {
      onCompleted: (resData) => {
        if (resData && resData.permissionCategories) {
          const data = new PermissionCategoryViewModel(
            resData.permissionCategories,
          );
          updatePermissionCategoryForEditUser(data.permissionCategories);
          setTotalPermissions(data.totalPermissions);
        }
      },
    },
  );

  const onGroupChecked = (group: string) => {
    const targetCategory = permissionCategories.find((pc) => pc.name === group);
    const areAnyPermissionsChecked = targetCategory?.permissions.some(
      (permission) => permission.isChecked,
    );
    const targetChecked = !areAnyPermissionsChecked;
    const permissionsToCheck = targetChecked
      ? new Set(
          resolvePermissionDependencies(
            targetCategory?.permissions.map(
              (permission) => permission.permission,
            ) || [],
            toPermissionCategoryResponses(permissionCategories),
          ),
        )
      : new Set(
          targetCategory?.permissions.map(
            (permission) => permission.permission,
          ),
        );

    const updated = permissionCategories.map((pc) => {
      if (pc.name === group) {
        return {
          ...pc,
          isChecked: targetChecked,
          permissions: pc.permissions.map((permission) => ({
            ...permission,
            isChecked: targetChecked,
          })),
        };
      }

      if (targetChecked) {
        const updatedPermissions = pc.permissions.map((permission) => ({
          ...permission,
          isChecked: permissionsToCheck.has(permission.permission)
            ? true
            : permission.isChecked,
        }));

        return {
          ...pc,
          permissions: updatedPermissions,
          isChecked: updatedPermissions.every(
            (permission) => permission.isChecked,
          ),
        };
      }

      return pc;
    });

    setPermissions(updated);
  };

  const onPermissionChecked = ({ permission }: PermissionCheckArg) => {
    const isCurrentlyChecked = permissionCategories.some((pc) =>
      pc.permissions.some(
        (entry) => entry.permission === permission && entry.isChecked,
      ),
    );
    const targetChecked = !isCurrentlyChecked;
    const permissionsToCheck = targetChecked
      ? new Set(
          resolvePermissionDependencies(
            [permission],
            toPermissionCategoryResponses(permissionCategories),
          ),
        )
      : new Set<string>();

    const updated = permissionCategories.map((pc) => {
      const updatedPermissions = pc.permissions.map((p) => {
        if (p.permission === permission) {
          return {
            ...p,
            isChecked: targetChecked,
          };
        }

        if (targetChecked && permissionsToCheck.has(p.permission)) {
          return {
            ...p,
            isChecked: true,
          };
        }

        return p;
      });

      return {
        ...pc,
        permissions: updatedPermissions,
        isChecked: updatedPermissions.every((entry) => entry.isChecked),
      };
    });

    setPermissions(updated);
  };

  const isMatchesWithSearchText = (text: string) =>
    text.toLowerCase().includes(searchedText.toLowerCase().trim());

  const filterBySearch = () =>
    [...permissionCategories].filter(
      (pc) =>
        isMatchesWithSearchText(pc.name) ||
        pc.permissions.find((p) => isMatchesWithSearchText(p.permission)),
    );

  const syncCheckboxStateWithAssigned = () => {
    // Always update the full catalog — search is display-only.
    setPermissions((current) =>
      applyAssignedChecks(current, assignedPermissionsRef.current),
    );
  };

  const filterPermissionCategoryBySearchText = () => {
    if (searchedText.trim() === '') {
      return { totalPermissions, permissionCategories };
    }

    const filteredData = filterBySearch();

    const totalPermissionsFiltered = filteredData.reduce((total, pc) => {
      return total + pc.permissions.length;
    }, 0);

    return {
      totalPermissions: totalPermissionsFiltered,
      permissionCategories: filteredData,
    };
  };

  const toggleAllPermissions = () => {
    const { permissionCategories: filteredPC } =
      filterPermissionCategoryBySearchText();

    const areSomeSelected = filteredPC.some((p) => p.isChecked);

    const updated = permissionCategories.map((pc) => {
      const isCategoryPresent = filteredPC.find((fpc) => pc.name === fpc.name);

      return {
        ...pc,
        isChecked: isCategoryPresent ? !areSomeSelected : pc.isChecked,
        permissions: pc.permissions.map((per) => ({
          ...per,
          isChecked: isCategoryPresent ? !areSomeSelected : per.isChecked,
        })),
      };
    });

    setPermissions(updated);
  };

  const onAddPermissions = () => {
    const reducer = (prev: AssignedPermissions[], pc: PermissionCategory) => {
      return [
        ...prev,
        ...pc.permissions.reduce((prev: AssignedPermissions[], p) => {
          if (p.isChecked) {
            return [...prev, { module: pc.name, permission: p.permission }];
          }
          return prev;
        }, []),
      ];
    };

    const checkedFromCatalog: AssignedPermissions[] =
      permissionCategories.reduce(reducer, []);

    const checkedPermissionSet = new Set(
      checkedFromCatalog.map((p) => p.permission),
    );

    const catalogPermissionSet = new Set(
      permissionCategories.flatMap((pc) =>
        pc.permissions.map((p) => p.permission),
      ),
    );
    const orphanAssigned = assignedPermissions.filter(
      (p) =>
        !checkedPermissionSet.has(p.permission) &&
        !catalogPermissionSet.has(p.permission),
    );

    const resolvedPermissions = resolvePermissionDependencies(
      checkedFromCatalog.map((entry) => entry.permission),
      toPermissionCategoryResponses(permissionCategories),
    );

    const resolvedAssigned = mapPermissionsToAssigned(
      resolvedPermissions,
      permissionCategories,
    );

    const data = dedupeAssignedPermissions([
      ...resolvedAssigned,
      ...orphanAssigned,
    ]);

    assignedPermissionsRef.current = data;
    setSearchedText('');
    setAssignedPermissions(data);
  };

  const filteredPC = filterPermissionCategoryBySearchText();

  const onPermissionDialogToggle = (isOpen = false) => {
    if (isOpen) {
      syncCheckboxStateWithAssigned();
    } else {
      setSearchedText('');
      // Discard unsaved checkbox toggles (e.g. Cancel / X after checking while searching).
      syncCheckboxStateWithAssigned();
    }
  };

  return {
    isLoading: loading,
    assignedPermissions,
    permissionCategories: filteredPC.permissionCategories,
    totalPermissions: filteredPC.totalPermissions,
    searchedText,
    onPermissionChecked,
    onGroupChecked,
    toggleAllPermissions,
    onAddPermissions,
    onPermissionDialogToggle,
    onSearch: setSearchedText,
  };
};

export default usePermissionsCategoryAPI;
